import { type AsyncResult, SynchrounousState, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { getGeoJsonCoverageBBox } from "@/cache/GeoJSONCache/tileUtils";
import { networkCaches } from "@/cache/GlobalCache";
import { emptyFeatureCollection } from "@/geojson";
import {
  DefaultLightningDuration,
  type LightningRenderMode,
  getLightningDurationMin,
  isLayerVisible,
  isLightningDuration,
  lightningColorRange,
} from "@/geojson/definitions";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { generateLightningListLayerCacheId } from "@/layers/geojson/LayerUtils";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { SENTRY_TRANSACTION_NAMES } from "@/sentry/TransactionConst";
import { captureTransaction } from "@/sentry/helpers";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import {
  Area,
  CoordinateSystem,
  type GeoJSONFeatureCollection,
  type LightningListRequest,
} from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { GuiTimeZone, LightningLayer } from "@mm/metx-workbench.meteomatics.com";
import { debounce } from "lodash";
import { type DateTime, Duration } from "luxon";
import mapboxgl, { type GeoJSONSourceSpecification, type MapMouseEvent, type MapboxGeoJSONFeature } from "mapbox-gl";
import {
  type MapboxLayerGroup,
  addLayerGroup,
  moveZIndex,
  removeLayerGroup,
  updateMapboxLayoutProperties,
  updateMapboxPaintProperties,
} from "../utility/mapbox-utils";
import {
  LIGHTNING_CLUSTER_SOURCE_MAX_ZOOM,
  LIGHTNING_COLOR_BUCKET_PROPERTY,
  LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP,
  type LightningLayerName,
  buildLightningHeatmapOpacity,
  createLightningClusterProperties,
  createLightningSelectedRangeFilter,
  createLightningSymbolColorExpression,
  createLightningTimerangeFilter,
  getLightningColorBucketIndex,
  getLightningRenderMode,
  getLightningSymbolIconSize,
  lightningHeatmapLayerDefs,
  lightningMapboxLayerGroup,
  matchesLightningTypeFilter,
} from "./custom/utils";
import { borderWidthFromOuterRadius, pieSlicePath } from "./earthquake/utils/iconUtils";
import { createLightingTooltip } from "./popovers";
import "./style/mapboxTablePopover.scss";

const logger = Logger.fromFilename(__filename);

export class LightningLayerImpl extends MapboxGeoJSONLayer<LightningLayer> {
  private static readonly clusterMarkerSyncDebounceMs = 120;
  private static readonly heatmapLayerNames = [
    "heatmap-layer-1",
    "heatmap-layer-2",
    "heatmap-layer-3",
    "heatmap-layer-4",
    "heatmap-layer-5",
    "heatmap-layer-6",
    "heatmap-layer-7",
  ] as const;

  private static readonly clusterAggregateCircleLayerName = "cluster-circle-layer" as const;
  private static readonly clusterCountLayerName = "cluster-count-layer" as const;
  private static readonly clusterSingleLayerName = "cluster-single-layer" as const;
  private static readonly symbolLayerName = "timerange-layer" as const;

  private checkTimeChanged: ChangeDetector<number> = new ChangeDetector<number>();
  private readonly beforeRenderTimeChanged: ChangeDetector<number> = new ChangeDetector<number>();
  private popup = new mapboxgl.Popup({ maxWidth: "600px", className: "mapbox-popover mapbox-popover-table" });
  private readonly clusteredSourceId: string;
  private readonly clusterPieMarkers = new Map<number, mapboxgl.Marker>();
  private lastClusteredSourceRawData: GeoJSONFeatureCollection | null = null;
  private lastClusteredSourceSelectedTimeMs: number | null = null;
  private lastClusteredSourceRenderKey: string | null = null;
  // Pie markers depend on rendered clusters and can update frequently during map
  // movement/source churn, so keep synchronization debounced.
  private readonly syncClusterPieMarkersDebounced = debounce(
    () => this.syncClusterPieMarkers(),
    LightningLayerImpl.clusterMarkerSyncDebounceMs,
    { maxWait: LightningLayerImpl.clusterMarkerSyncDebounceMs },
  );

  private mapboxLayerGroup: MapboxLayerGroup<LightningLayerName>;

  private readonly onMouseEnter = (e: MapMouseEvent) => {
    const features = (e.features ?? []) as MapboxGeoJSONFeature[];
    if (!features.length) {
      return;
    }
    this.scene.getMapboxMap().getCanvas().style.cursor = "pointer";
  };

  private readonly onMouseLeave = () => {
    this.scene.getMapboxMap().getCanvas().style.cursor = "";
  };

  private readonly onClick = (e: MapMouseEvent) => {
    const features = (e.features ?? []) as MapboxGeoJSONFeature[];
    if (!features.length) {
      return;
    }

    const feature = features[0];
    const pointCount = Number(feature.properties?.point_count);
    if (Number.isFinite(pointCount)) {
      const clusterId = Number(feature.properties?.cluster_id);
      if (Number.isFinite(clusterId) && feature.geometry.type === "Point") {
        this.expandCluster(clusterId, feature.geometry.coordinates as [number, number]);
      }
      return;
    }

    if (feature.geometry.type === "Point") {
      const coordinates = feature.geometry.coordinates;
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      this.popup
        .setLngLat(new mapboxgl.LngLat(coordinates[0], coordinates[1]))
        .setHTML(createLightingTooltip(features, this.timezone))
        .addTo(this.scene.getMapboxMap());
      return;
    }

    logger.error(
      "The clicked lightning feature did not contain 'Point' type geometry, instead contained:",
      feature.geometry.type,
      feature,
    );
  };

  private readonly onSourceData = (e: any) => {
    if (!this.shouldRenderPieClusters()) {
      return;
    }
    if (e?.sourceId !== this.clusteredSourceId) {
      return;
    }
    if (e?.sourceDataType !== "content") {
      return;
    }
    this.syncClusterPieMarkersDebounced();
  };

  private readonly onViewportChanged = () => {
    if (!this.shouldRenderPieClusters()) {
      return;
    }
    this.syncClusterPieMarkersDebounced();
  };

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  constructor(id: number, props: LightningLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);

    // Clustered rendering uses a dedicated derived source, while symbol/heatmap
    // layers continue to consume the raw lightning source.
    this.clusteredSourceId = `${this.getMapboxSource()}-clustered`;
    this.addClusteredSource();

    const source = this.getMapboxSource();
    this.mapboxLayerGroup = lightningMapboxLayerGroup(
      this.mapbox_id,
      props,
      source,
      this.clusteredSourceId,
      this.getRequestDateTime(this.scene.getDisplayTimeWithOffset()),
      props.parameter_unit,
    );

    addLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.initialSpecs());
    this.updateLightningSymbolZoomRange();
    this.setEventListeners();
    this.updateLightningVisible();
  }

  humanReadableId(): string {
    return safeFmt`metx.LightningLayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  checker(): PropsChecker<LightningLayer, LayerBase<LightningLayer>> {
    type CheckerProps = CheckedProps<LightningLayer>;
    const map = this.scene.getMapboxMap();

    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "model":
          case "calibrated":
          case "custom_options":
          case "legend_visible":
          case "parameter_unit":
            this.updateLightningVisible();
            this.updateLightningTimerangeFilter();
            this.updateLightningSymbolZoomRange();
            break;
          case "ens_select":
            break;
          case "show":
            this.updateLightningTimerangeFilter();
            updateMapboxLayoutProperties(map, this.mapboxLayerGroup.allIds(), {
              visibility: curr.show ? "visible" : "none",
            });
            this.updateLightningVisible();
            this.updateLightningSymbolZoomRange();
            break;
          case "opacity":
            updateMapboxPaintProperties(map, this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName), {
              "icon-opacity": curr[property],
            });
            updateMapboxPaintProperties(map, this.mapboxLayerGroup.id(...LightningLayerImpl.heatmapLayerNames), {
              "heatmap-opacity": buildLightningHeatmapOpacity(curr[property]),
            });
            updateMapboxPaintProperties(map, this.mapboxLayerGroup.id(LightningLayerImpl.clusterSingleLayerName), {
              "circle-opacity": curr[property],
              "circle-stroke-opacity": curr[property],
            });
            this.updateClusterAggregateLayerOpacity(this.shouldRenderPieClusters());
            this.syncClusterPieMarkers();
            break;
          case "text_size":
            updateMapboxLayoutProperties(map, this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName), {
              "icon-size": getLightningSymbolIconSize(curr[property]),
            });
            break;
          case "text_color":
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      legend_visible: checkLayerProp("legend_visible"),
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      parameter_unit: checkLayerProp("parameter_unit"),
      show: checkLayerProp("show"),
      text_size: checkLayerProp("text_size"),
      text_color: checkLayerProp("text_color"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  setEventListeners() {
    const map = this.scene.getMapboxMap();
    const interactiveLayerIds = this.mapboxLayerGroup.id(
      LightningLayerImpl.symbolLayerName,
      LightningLayerImpl.clusterSingleLayerName,
      LightningLayerImpl.clusterAggregateCircleLayerName,
      LightningLayerImpl.clusterCountLayerName,
    );

    map.on("mouseenter", interactiveLayerIds, this.onMouseEnter);
    map.on("mouseleave", interactiveLayerIds, this.onMouseLeave);
    map.on("click", interactiveLayerIds, this.onClick);
    map.on("sourcedata", this.onSourceData);
    map.on("moveend", this.onViewportChanged);
    map.on("zoomend", this.onViewportChanged);
  }

  createRequest(dateTimeWithOffsetTime: DateTime, v2?: boolean): LightningListRequest<CoordinateSystem.WGS84> {
    // Snap the request area to a fixed tile window so cache keys are stable while panning.
    const { area: viewportArea } = this.computeBoundingBox(0);
    const tiledCoverage = getGeoJsonCoverageBBox(
      {
        west: viewportArea.west,
        east: viewportArea.east,
        south: viewportArea.south,
        north: viewportArea.north,
      },
      this.props.kind as LayerKindName,
    );
    const area = tiledCoverage
      ? new Area(CoordinateSystem.WGS84, {
          west: tiledCoverage.west,
          east: tiledCoverage.east,
          south: tiledCoverage.south,
          north: tiledCoverage.north,
        })
      : viewportArea;

    const timeDuration = Duration.fromObject({ minutes: getLightningDurationMin(this.props.parameter_unit) });

    return {
      area: area,
      datetime: dateTimeWithOffsetTime,
      timeDuration: timeDuration,
      // CSV is significantly smaller on the wire for lightning payloads.
      format: "csv",
      enable_feature: !!v2,
    };
  }

  private getRequestDateTime(dateTime: DateTime): DateTime {
    const base = roundToNearestCacheableTime(dateTime, this.props.kind as LayerKindName);
    // During animation we snap to minute boundaries so cache keys and prefetch
    // bookkeeping align with frame timestamps.
    return this.scene.isAnimation() ? base.startOf("minute") : base;
  }

  updateTrigger() {
    const dateTimeWithOffsetTime = this.getRequestDateTime(this.scene.getDisplayTimeWithOffset());
    const request = this.createRequest(dateTimeWithOffsetTime, !!this.props.custom_options?.lightning_v2);
    const result = networkCaches.geojson_cache.retrieveLightningList(request, {
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(result);

    const timeChanged = this.checkTimeChanged.changed(dateTimeWithOffsetTime.toMillis());
    const keepLastDataWhilePending = this.scene.isAnimation();
    const usesClusterSource = this.usesClusterSource();
    if (usesClusterSource) {
      // Keep cluster source synchronized even while raw source update is pending,
      // so switching render mode does not show stale/empty clusters.
      this.syncClusterSourceWithResult(result, dateTimeWithOffsetTime, {
        keepLastDataWhilePending,
        timeChanged,
      });
    } else {
      this.updateClusteredSourceData(emptyFeatureCollection);
      this.clearClusterPieMarkers();
    }

    if (timeChanged) {
      this.updateLightningTimerangeFilter({ refreshClusterSource: false });
    }

    this.updateSource(
      result,
      dateTimeWithOffsetTime,
      usesClusterSource
        ? (data) => {
            this.updateClusteredSourceDataForFrame(data, dateTimeWithOffsetTime);
          }
        : undefined,
      {
        keepLastDataWhilePending,
        timeChangedOverride: timeChanged,
      },
    );
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffsetTime = this.getRequestDateTime(this.scene.getDateTimeWithOffset(timeFrame));
    const request = this.createRequest(dateTimeWithOffsetTime, !!this.props.custom_options?.lightning_v2);
    const tags = {
      layerKind: `${this.props.kind ? this.props.kind : "undefined"}`,
      parameter: `${this.props.parameter_unit}`,
      requestedDate: `${dateTimeWithOffsetTime}`,
    };
    const result = networkCaches.geojson_cache.retrieveLightningList(request);

    if (!this.scene.isAnimation()) {
      return asVoidAsyncResult(
        result,
        captureTransaction(result.asynchronous, SENTRY_TRANSACTION_NAMES.LIGHTNING_LAYER_LOADING_SPEED, tags),
      );
    }

    return asVoidAsyncResult(result);
  }

  isRebuffering(time: DateTime): boolean {
    // Keep readiness checks aligned with fetch/update-trigger request timestamps.
    // Otherwise prefetch can mark a frame as "ready" too early and animation switches
    // before lightning data for the effective frame time is available.
    const dateTimeWithOffset = this.getRequestDateTime(this.scene.getDateTimeWithOffset(time));
    const request = this.createRequest(dateTimeWithOffset, !!this.props.custom_options?.lightning_v2);
    const { synchronous } = this.peekLayerCache(request);
    return synchronous === SynchrounousState.StillPending;
  }

  beforeRender(): void {
    if (!this.props.show) {
      return;
    }
    if (this.scene.isAnimation()) {
      this.setLayerZoomRangeIfNeeded(3, 24);
      this.updateTrigger();
    } else {
      this.setLayerZoomRangeIfNeeded(0, 24);
      const dateTimeWithOffsetTime = this.getRequestDateTime(this.scene.getDisplayTimeWithOffset());
      const timeChanged = this.beforeRenderTimeChanged.changed(dateTimeWithOffsetTime.toMillis());
      if (timeChanged) {
        this.debouncedUpdateTrigger.cancel();
        this.updateTrigger();
        return;
      }
      this.debouncedUpdateTrigger();
    }
  }

  // beforeRender is called frequency there for we call also setLayerZoomRangeIfNeeded every time,
  // to avoid the mapbox needs to rerender and never goes into idle we compare if
  // we changed the zoom range for this layer already
  // this way we can to the image export with the idle event
  setLayerZoomRangeIfNeeded(minZoom: number, maxZoom: number) {
    const layer: mapboxgl.AnyLayer | undefined = this.scene.getMapboxMap().getLayer(this.mapbox_id);
    if (layer && "minzoom" in layer && "maxzoom" in layer) {
      if (layer.minzoom !== minZoom || layer.maxzoom !== maxZoom) {
        this.scene.getMapboxMap().setLayerZoomRange(this.mapbox_id, minZoom, maxZoom);
      }
    }
  }

  get mapboxIndex(): string {
    return this.mapboxLayerGroup.mainId();
  }

  peekLayerCache(request: LightningListRequest<CoordinateSystem.WGS84>): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateLightningListLayerCacheId(request);
    return networkCaches.geojson_cache.peekCache(cacheId);
  }

  removeLayer(): void {
    removeLayerGroup(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds());
    const map = this.scene.getMapboxMap();
    this.syncClusterPieMarkersDebounced.cancel();
    this.clearClusterPieMarkers();

    const interactiveLayerIds = this.mapboxLayerGroup.id(
      LightningLayerImpl.symbolLayerName,
      LightningLayerImpl.clusterSingleLayerName,
      LightningLayerImpl.clusterAggregateCircleLayerName,
      LightningLayerImpl.clusterCountLayerName,
    );
    map.off("mouseenter", interactiveLayerIds, this.onMouseEnter);
    map.off("mouseleave", interactiveLayerIds, this.onMouseLeave);
    map.off("click", interactiveLayerIds, this.onClick);
    map.off("sourcedata", this.onSourceData);
    map.off("moveend", this.onViewportChanged);
    map.off("zoomend", this.onViewportChanged);

    if (map.getSource(this.clusteredSourceId)) {
      map.removeSource(this.clusteredSourceId);
    }

    super.removeLayer();
  }

  moveZIndex(beforeLayerId?: string | undefined): void {
    moveZIndex(this.scene.getMapboxMap(), this.mapboxLayerGroup.allIds(), beforeLayerId);
  }

  updateLightningVisible() {
    const map = this.scene.getMapboxMap();
    const selectedDuration = isLightningDuration(this.props.parameter_unit)
      ? this.props.parameter_unit
      : DefaultLightningDuration;
    const mode = this.getRenderMode();
    const showHeatmap = mode === "heatmap";
    const showClusterCircles = mode === "cluster";
    const showClusterCount = mode === "cluster";
    const showSymbols = true;

    for (const layer of lightningHeatmapLayerDefs) {
      map.setLayoutProperty(
        this.mapboxLayerGroup.id(layer.heatmap)[0],
        "visibility",
        this.props.show && showHeatmap && isLayerVisible(selectedDuration, layer.duration) ? "visible" : "none",
      );
    }

    map.setLayoutProperty(
      this.mapboxLayerGroup.id(LightningLayerImpl.clusterAggregateCircleLayerName)[0],
      "visibility",
      this.props.show && showClusterCircles ? "visible" : "none",
    );
    map.setLayoutProperty(
      this.mapboxLayerGroup.id(LightningLayerImpl.clusterSingleLayerName)[0],
      "visibility",
      this.props.show && showClusterCircles ? "visible" : "none",
    );
    map.setLayoutProperty(
      this.mapboxLayerGroup.id(LightningLayerImpl.clusterCountLayerName)[0],
      "visibility",
      this.props.show && showClusterCount ? "visible" : "none",
    );
    this.updateClusterAggregateLayerOpacity(this.shouldRenderPieClusters());
    if (this.shouldRenderPieClusters()) {
      this.syncClusterPieMarkers();
    } else {
      this.clearClusterPieMarkers();
    }

    map.setLayoutProperty(
      this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName)[0],
      "visibility",
      this.props.show && showSymbols ? "visible" : "none",
    );
  }

  updateLightningSymbolZoomRange() {
    const map = this.scene.getMapboxMap();
    const mode = this.getRenderMode();
    const minZoom = mode === "none" ? 0 : LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP;
    const maxZoom = 24;

    map.setLayerZoomRange(this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName)[0], minZoom, maxZoom);
  }

  updateLightningTimerangeFilter(options: { refreshClusterSource?: boolean } = {}) {
    const refreshClusterSource = options.refreshClusterSource ?? true;
    const map = this.scene.getMapboxMap();
    const displayTimeWithOffset = this.getRequestDateTime(this.scene.getDisplayTimeWithOffset());
    const selectedDuration = isLightningDuration(this.props.parameter_unit)
      ? this.props.parameter_unit
      : DefaultLightningDuration;
    const lightningTypeFilter = this.readLightningTypeFilter();

    for (const layer of lightningHeatmapLayerDefs) {
      const filter = createLightningTimerangeFilter(displayTimeWithOffset, layer.duration, lightningTypeFilter);
      map.setFilter(this.mapboxLayerGroup.id(layer.heatmap)[0], filter);
    }

    map.setFilter(
      this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName)[0],
      createLightningSelectedRangeFilter(displayTimeWithOffset, selectedDuration, lightningTypeFilter),
    );
    map.setPaintProperty(
      this.mapboxLayerGroup.id(LightningLayerImpl.symbolLayerName)[0],
      "icon-color",
      createLightningSymbolColorExpression(displayTimeWithOffset),
    );

    if (refreshClusterSource && this.usesClusterSource()) {
      this.refreshClusterSourceFromLatestData(displayTimeWithOffset);
    } else if (!this.usesClusterSource()) {
      this.updateClusteredSourceData(emptyFeatureCollection);
      this.clearClusterPieMarkers();
    }
  }

  private getRenderMode(): LightningRenderMode {
    return getLightningRenderMode(this.props);
  }

  private usesClusterSource(): boolean {
    const mode = this.getRenderMode();
    return mode === "cluster";
  }

  private clusteredSourceSpec(): GeoJSONSourceSpecification {
    return {
      type: "geojson",
      data: emptyFeatureCollection,
      cluster: true,
      clusterRadius: 30,
      clusterMaxZoom: LIGHTNING_CLUSTER_SOURCE_MAX_ZOOM,
      clusterProperties: createLightningClusterProperties(),
    };
  }

  private addClusteredSource(): void {
    const map = this.scene.getMapboxMap();
    if (map.getSource(this.clusteredSourceId)) {
      map.removeSource(this.clusteredSourceId);
    }
    map.addSource(this.clusteredSourceId, this.clusteredSourceSpec());
  }

  private updateClusteredSourceData(data: GeoJSONFeatureCollection): void {
    const source = this.scene.getMapboxMap().getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
    source?.setData(data as GeoJSON.FeatureCollection<GeoJSON.Geometry>);
    this.lastClusteredSourceRawData = null;
    this.lastClusteredSourceSelectedTimeMs = null;
    this.lastClusteredSourceRenderKey = null;
  }

  /**
   * Updates the clustered Mapbox source only when the raw data, frame time, or
   * render-affecting options changed. Avoiding redundant `setData` calls keeps
   * animation playback from retriggering cluster layout work every frame tick.
   */
  private updateClusteredSourceDataForFrame(data: GeoJSONFeatureCollection, selectedTime: DateTime): void {
    const selectedTimeMs = selectedTime.toMillis();
    const renderKey = this.getClusteredSourceRenderKey();
    if (
      this.lastClusteredSourceRawData === data &&
      this.lastClusteredSourceSelectedTimeMs === selectedTimeMs &&
      this.lastClusteredSourceRenderKey === renderKey
    ) {
      return;
    }

    const source = this.scene.getMapboxMap().getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
    source?.setData(this.toClusterFeatureCollection(data, selectedTime) as GeoJSON.FeatureCollection<GeoJSON.Geometry>);
    this.lastClusteredSourceRawData = data;
    this.lastClusteredSourceSelectedTimeMs = selectedTimeMs;
    this.lastClusteredSourceRenderKey = renderKey;
  }

  /**
   * Captures props that affect how raw lightning features are filtered before
   * clustering, so memoized cluster source data is invalidated when they change.
   */
  private getClusteredSourceRenderKey(): string {
    return JSON.stringify({
      parameterUnit: this.props.parameter_unit,
      lightningTypeFilter: this.readLightningTypeFilter(),
    });
  }

  private toClusterFeatureCollection(data: GeoJSONFeatureCollection, selectedTime: DateTime): GeoJSONFeatureCollection {
    const selectedDuration = isLightningDuration(this.props.parameter_unit)
      ? this.props.parameter_unit
      : DefaultLightningDuration;
    const lightningTypeFilter = this.readLightningTypeFilter();
    const selectedTimeMs = selectedTime.valueOf();
    const lowerBound =
      selectedTimeMs - Duration.fromObject({ minutes: getLightningDurationMin(selectedDuration) }).valueOf();

    const features = (data.features ?? [])
      .map((feature: any) => {
        const strokeTimeMs = Number(feature?.properties?.stroke_time_ms);
        if (!Number.isFinite(strokeTimeMs)) {
          return null;
        }
        if (strokeTimeMs < lowerBound || strokeTimeMs >= selectedTimeMs) {
          return null;
        }
        if (!matchesLightningTypeFilter(lightningTypeFilter, feature?.properties?.lightning_type)) {
          return null;
        }

        return {
          ...feature,
          properties: {
            ...(feature.properties || {}),
            // Precompute age bucket for mapbox cluster aggregation and marker colors.
            [LIGHTNING_COLOR_BUCKET_PROPERTY]: getLightningColorBucketIndex(strokeTimeMs, selectedTime),
          },
        };
      })
      .filter(Boolean) as GeoJSON.Feature[];

    return {
      ...data,
      features,
    } as GeoJSONFeatureCollection;
  }

  private syncClusterSourceWithResult(
    result: AsyncResult<GeoJSONFeatureCollection>,
    selectedTime: DateTime,
    options: { keepLastDataWhilePending: boolean; timeChanged: boolean },
  ): void {
    const data = result.synchronous;
    if (data === SynchrounousState.NotRequested || data === SynchrounousState.PermanentlyFailed) {
      this.updateClusteredSourceData(emptyFeatureCollection);
      return;
    }
    if (data === SynchrounousState.StillPending) {
      if (options.timeChanged && !options.keepLastDataWhilePending) {
        this.updateClusteredSourceData(emptyFeatureCollection);
      }
      return;
    }

    this.updateClusteredSourceDataForFrame(data, selectedTime);
  }

  private refreshClusterSourceFromLatestData(selectedTime: DateTime): void {
    const latest = this.getLatestSourceData().synchronous;
    if (
      latest === SynchrounousState.NotRequested ||
      latest === SynchrounousState.StillPending ||
      latest === SynchrounousState.PermanentlyFailed
    ) {
      return;
    }
    this.updateClusteredSourceDataForFrame(latest, selectedTime);
  }

  private readLightningTypeFilter(): string[] | null {
    const customOptions = this.props.custom_options as { lightning_type_filter?: string[] | null } | undefined;
    const filter = customOptions?.lightning_type_filter;
    return Array.isArray(filter) ? filter : null;
  }

  private expandCluster(clusterId: number, center: [number, number]): void {
    const source = this.scene.getMapboxMap().getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
    if (!source) {
      return;
    }

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null) {
        return;
      }
      this.scene.getMapboxMap().easeTo({ center, zoom });
    });
  }

  private shouldRenderPieClusters(): boolean {
    return this.getRenderMode() === "cluster" && this.props.show;
  }

  private updateClusterAggregateLayerOpacity(showPieClusters: boolean): void {
    const map = this.scene.getMapboxMap();
    // Hide mapbox cluster circles/text when custom HTML pie markers are enabled.
    const opacity = showPieClusters ? 0 : this.props.opacity ?? 1;
    const clusterCircleLayerId = this.mapboxLayerGroup.id(LightningLayerImpl.clusterAggregateCircleLayerName)[0];
    const clusterCountLayerId = this.mapboxLayerGroup.id(LightningLayerImpl.clusterCountLayerName)[0];

    if (map.getLayer(clusterCircleLayerId)) {
      map.setPaintProperty(clusterCircleLayerId, "circle-opacity", opacity);
      map.setPaintProperty(clusterCircleLayerId, "circle-stroke-opacity", opacity);
    }
    if (map.getLayer(clusterCountLayerId)) {
      map.setPaintProperty(clusterCountLayerId, "text-opacity", opacity);
    }
  }

  private clearClusterPieMarkers(): void {
    for (const marker of this.clusterPieMarkers.values()) {
      marker.remove();
    }
    this.clusterPieMarkers.clear();
  }

  private clusterRadiusFromPointCount(pointCount: number): number {
    if (pointCount < 10) {
      return 16;
    }
    if (pointCount < 50) {
      return 20;
    }
    if (pointCount < 100) {
      return 26;
    }
    return 32;
  }

  private computeClusterSlices(
    props: Record<string, unknown>,
    pointCount: number,
  ): Array<{ value: number; color: string }> {
    const slices = lightningColorRange
      .map((color, index) => ({
        value: Number(props[`lightning_bucket_count_${index}`] ?? 0),
        color,
      }))
      .filter((slice) => Number.isFinite(slice.value) && slice.value > 0);

    if (!slices.length) {
      return [{ value: pointCount, color: lightningColorRange[lightningColorRange.length - 1] }];
    }

    return slices;
  }

  private buildClusterPieMarker(pointCount: number, slices: Array<{ value: number; color: string }>): HTMLElement {
    const outerRadius = this.clusterRadiusFromPointCount(pointCount);
    const size = Math.max(16, outerRadius * 2);
    const center = size / 2;
    const borderWidth = borderWidthFromOuterRadius(outerRadius);
    const pieRadius = Math.max(0, outerRadius - borderWidth / 2);
    const labelFontSize = Math.max(8, Math.round(size * 0.3));

    let currentAngle = -Math.PI / 2;
    const pathNodes: string[] = [];

    if (slices.length === 1) {
      pathNodes.push(`<circle cx="${center}" cy="${center}" r="${pieRadius}" fill="${slices[0].color}" />`);
    } else {
      const total = slices.reduce((sum, slice) => sum + slice.value, 0);
      for (const slice of slices) {
        const ratio = total > 0 ? slice.value / total : 0;
        const span = ratio * Math.PI * 2;
        const startAngle = currentAngle;
        const endAngle = currentAngle + span;

        pathNodes.push(
          `<path d="${pieSlicePath(center, center, pieRadius, startAngle, endAngle)}" fill="${slice.color}" />`,
        );
        currentAngle = endAngle;
      }
    }

    const marker = document.createElement("div");
    marker.style.width = `${size}px`;
    marker.style.height = `${size}px`;
    marker.style.cursor = "pointer";
    marker.style.opacity = String(this.props.opacity ?? 1);
    marker.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">${pathNodes.join(
      "",
    )}<circle cx="${center}" cy="${center}" r="${pieRadius}" fill="none" stroke="#ffffff" stroke-width="${borderWidth}"/><text x="${center}" y="${
      center + 1
    }" fill="#ffffff" stroke="rgba(0,0,0,0.45)" stroke-width="2" paint-order="stroke" font-size="${labelFontSize}" text-anchor="middle" dominant-baseline="middle" font-weight="700">${pointCount}</text></svg>`;
    return marker;
  }

  private syncClusterPieMarkers(): void {
    if (!this.shouldRenderPieClusters()) {
      this.clearClusterPieMarkers();
      return;
    }

    const map = this.scene.getMapboxMap();
    if (!map.getSource(this.clusteredSourceId)) {
      this.clearClusterPieMarkers();
      return;
    }

    let features: mapboxgl.MapboxGeoJSONFeature[] = [];
    try {
      features = map.queryRenderedFeatures({
        layers: [this.mapboxLayerGroup.id(LightningLayerImpl.clusterAggregateCircleLayerName)[0]],
      }) as mapboxgl.MapboxGeoJSONFeature[];
    } catch (_error) {
      features = [];
    }

    if (!features.length) {
      this.clearClusterPieMarkers();
      return;
    }

    const uniqueClusters = new Map<number, mapboxgl.MapboxGeoJSONFeature>();
    for (const feature of features) {
      const pointCount = Number(feature.properties?.point_count);
      if (!Number.isFinite(pointCount)) {
        continue;
      }
      const clusterId = Number(feature.properties?.cluster_id);
      if (!Number.isFinite(clusterId)) {
        continue;
      }
      if (!uniqueClusters.has(clusterId)) {
        uniqueClusters.set(clusterId, feature);
      }
    }

    // Rebuild marker set from current rendered clusters to avoid stale marker
    // positions/contents after pan/zoom/filter/source updates.
    this.clearClusterPieMarkers();

    for (const [clusterId, feature] of uniqueClusters) {
      if (feature.geometry.type !== "Point") {
        continue;
      }

      const coords = feature.geometry.coordinates as [number, number];
      const pointCount = Number(feature.properties?.point_count ?? 0);
      if (!Number.isFinite(pointCount) || pointCount <= 0) {
        continue;
      }

      const slices = this.computeClusterSlices((feature.properties || {}) as Record<string, unknown>, pointCount);
      const markerEl = this.buildClusterPieMarker(pointCount, slices);
      markerEl.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.expandCluster(clusterId, coords);
      });

      const marker = new mapboxgl.Marker({ element: markerEl, anchor: "center" }).setLngLat(coords).addTo(map);
      this.clusterPieMarkers.set(clusterId, marker);
    }
  }
}
